import { useEffect, useState } from "react";
import api, { apiErr } from "../lib/api";
import { capture, EVENTS } from "../analytics/posthog";
import { getReferralTid } from "../analytics/firstPromoter";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Check, Coins, Info } from "lucide-react";

export default function Billing() {
  const [settings, setSettings] = useState(null);
  const [balance, setBalance] = useState({ credits: 0, plan: "free" });
  const [ledger, setLedger] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const [{ data: s }, { data: b }, { data: l }] = await Promise.all([
        api.get("/settings"), api.get("/credits/balance"), api.get("/credits/ledger"),
      ]);
      setSettings(s); setBalance(b); setLedger(l);
    } catch (e) { toast.error(apiErr(e)); }
  };
  useEffect(() => { load(); }, []);

  // Return from Stripe Checkout: poll server-verified status, then refresh balance.
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const status = p.get("payment"), sid = p.get("session_id");
    if (status === "success" && sid) {
      let tries = 0;
      const tick = async () => {
        tries++;
        try {
          const { data } = await api.get(`/billing/status/${sid}`);
          if (data.status === "paid") {
            // Fire subscription_activated ONLY for a subscription checkout confirmed
            // paid by the backend/webhook (status=paid), not merely the success URL.
            try {
              const last = JSON.parse(window.localStorage.getItem("ind_last_checkout") || "null");
              if (last && last.kind === "subscription") capture(EVENTS.subscriptionActivated, { plan: last.key });
              window.localStorage.removeItem("ind_last_checkout");
            } catch {}
            toast.success("Pago confirmado. Créditos actualizados."); window.dispatchEvent(new Event("credits:refresh")); await load(); window.history.replaceState({}, "", "/app/billing"); return;
          }
        } catch {}
        if (tries < 8) setTimeout(tick, 1500);
        else { toast.message("Procesando el pago… tu saldo se actualizará en breve."); await load(); }
      };
      tick();
    } else if (status === "cancelled") {
      capture(EVENTS.checkoutCancelled);
      toast.message("Pago cancelado."); window.history.replaceState({}, "", "/app/billing");
    }
  }, []);

  const checkout = async (kind, key) => {
    setBusy(key);
    try {
      capture(EVENTS.checkoutStarted, { kind, plan: kind === "subscription" ? key : undefined, pack: kind === "topup" ? key : undefined });
      try { window.localStorage.setItem("ind_last_checkout", JSON.stringify({ kind, key })); } catch {}
      const payload = { kind, key, origin_url: window.location.origin };
      // Affiliate attribution: only for subscriptions, only if a real tid exists.
      if (kind === "subscription") {
        const tid = getReferralTid();
        if (tid) payload.fp_tid = tid;
      }
      const { data } = await api.post("/billing/checkout", payload);
      if (data.url) window.location.href = data.url;
    } catch (e) {
      capture(EVENTS.checkoutFailed, { kind, status: e?.response?.status || "network" });
      toast.error(apiErr(e)); setBusy(null);
    }
  };
  const topup = (pack) => checkout("topup", pack);
  const subscribe = (plan) => checkout("subscription", plan);
  const openPortal = async () => {
    try { const { data } = await api.post("/billing/portal", { origin_url: window.location.origin }); if (data.url) window.location.href = data.url; }
    catch (e) { toast.error(apiErr(e)); }
  };

  const plans = settings?.pricing?.plans || [];
  const topups = settings?.pricing?.topups || [];

  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-head text-2xl sm:text-3xl font-bold text-white">Facturación y créditos</h1>
        <p className="mt-1 text-slate-400">Suscripción para la plataforma y créditos para el trabajo de Architect.</p>
      </div>

      <div className="card-arch p-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-amber-500/15"><Coins className="h-6 w-6 text-amber-400" /></div>
          <div><p className="mono-label text-slate-400">Saldo actual</p><p className="font-head text-3xl font-extrabold text-white">{balance.credits} <span className="text-base text-slate-500">créditos</span></p></div>
        </div>
        <span className="rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-xs font-mono text-slate-300">Plan: {balance.plan}</span>
      </div>

      <div className="card-arch p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <p className="text-sm text-slate-300">Architect Build = <b className="text-white">15 créditos</b> · Rediseño sustancial = <b className="text-white">5 créditos</b>. El diagnóstico, el chat, la implementación y la verificación son gratis.</p>
        {balance.plan !== "free" && <Button data-testid="billing-portal" onClick={openPortal} variant="outline" className="bg-white/[0.03] border-white/10 text-white rounded-full shrink-0">Gestionar suscripción</Button>}
      </div>

      {/* Plans */}
      <div>
        <h2 className="font-head text-lg font-semibold text-white">Planes de suscripción</h2>
        <div className="mt-4 grid md:grid-cols-3 gap-5">
          {plans.map((p, i) => (
            <div key={p.id} className={`card-arch p-6 ${i === 1 ? "border-amber-500/40" : ""}`}>
              <h3 className="font-head text-xl font-bold text-white">{p.name}</h3>
              <p className="mt-2"><span className="font-head text-3xl font-extrabold text-white">{p.price}€</span><span className="text-slate-500">/mes</span></p>
              <p className="mt-1 text-sm text-cyan-400 font-mono">{p.credits} créditos/mes</p>
              <p className="text-xs text-slate-500">≈ hasta {Math.floor(p.credits / 15)} Architect Builds/mes</p>
              <ul className="mt-4 space-y-2">{p.features?.map((f) => <li key={f} className="flex gap-2 text-sm text-slate-300"><Check className="h-4 w-4 text-emerald-400 shrink-0 mt-0.5" />{f}</li>)}</ul>
              <Button data-testid={`billing-plan-${p.id}`} onClick={() => subscribe(p.id)} disabled={busy === p.id || balance.plan === p.id} className="mt-5 w-full bg-amber-500 text-black hover:bg-amber-400 rounded-full font-semibold disabled:opacity-50">{balance.plan === p.id ? "Plan actual" : busy === p.id ? "…" : "Suscribirme"}</Button>
            </div>
          ))}
        </div>
      </div>

      {/* Top-ups */}
      <div>
        <h2 className="font-head text-lg font-semibold text-white">Top-ups de créditos</h2>
        <div className="mt-4 grid sm:grid-cols-3 gap-5">
          {topups.map((t) => (
            <div key={t.id} className="card-arch p-6 text-center">
              <p className="font-head text-3xl font-extrabold text-amber-400">{t.credits}</p>
              <p className="text-sm text-slate-500">créditos · {t.price}€</p>
              <Button data-testid={`billing-topup-${t.id}`} onClick={() => topup(t.id)} disabled={busy === t.id} className="mt-4 w-full bg-amber-500 text-black hover:bg-amber-400 rounded-full font-semibold">{busy === t.id ? "…" : "Añadir"}</Button>
            </div>
          ))}
        </div>
      </div>

      {/* Ledger */}
      <div>
        <h2 className="font-head text-lg font-semibold text-white">Historial de créditos</h2>
        <div className="mt-4 card-arch divide-y divide-white/5">
          {ledger.length === 0 && <p className="p-5 text-sm text-slate-500">Sin movimientos.</p>}
          {ledger.map((e) => (
            <div key={e.id} className="flex items-center justify-between p-4" data-testid={`ledger-row-${e.id}`}>
              <div><p className="text-sm text-white">{e.source}</p><p className="text-xs text-slate-500 font-mono">{e.type} · {new Date(e.timestamp).toLocaleString("es-ES")}</p></div>
              <div className="text-right"><p className={`font-mono font-semibold ${e.amount >= 0 ? "text-emerald-400" : "text-red-400"}`}>{e.amount >= 0 ? "+" : ""}{e.amount}</p><p className="text-xs text-slate-500">saldo {e.balance_after}</p></div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
